<template>
  <CContainer
    class="datatable-container pt-1 pt-lg-0 mt-lg-3 px-0 px-md-2 pb-3"
  >
    <CRow>
      <CCol id="periods-form" md="5">
        <h1 class="text-center mt-4 mt-md-0">
          {{
            editablePeriod && editablePeriod.id
              ? "Edit Period"
              : "Create Period"
          }}
          <HelpCenterButton classes="ms-2" content-key="periods_create" />
        </h1>
        <div class="form-group">
          <CFormInput
            placeholder="Name"
            :class="{
              'is-invalid': v$.form.name.$error || reqResponse.errors['name']
            }"
            v-model="form.name"
          />
          <div
            v-if="
              (v$.form.name.$error && v$.form.name.maxLength.$invalid) ||
              reqResponse.errors['name']
            "
            class="invalid-feedback"
          >
            The name may not be greater than 40 characters.
          </div>
          <div
            v-if="v$.form.name.$error && v$.form.name.required.$invalid"
            class="invalid-feedback"
          >
            This field is required
          </div>
        </div>
        <div class="form-group">
          <VSelect
            v-model="form.status"
            class="cs-select mb-3 ps__child--consume"
            :options="periodStatuses"
            placeholder="Status"
            :clearable="false"
            :searchable="false"
            :reduce="(item) => item.value"
          />
          <div
            v-if="v$.form.status.$error && v$.form.status.required.$invalid"
            class="invalid-feedback"
          >
            This field is required
          </div>
        </div>
        <div
          v-if="editablePeriod && editablePeriod.id"
          class="d-flex justify-content-center justify-content-md-start"
        >
          <div @click="submit(false)">
            <CLoadingButton
              :spinner="isLoading"
              :disabled="isLoading"
              :loading="isLoading"
              class="me-2 btn bg-gradient-blue px-3 px-md-4 text-white"
            >
              Update
            </CLoadingButton>
          </div>
          <div @click="deletePeriod()">
            <button class="me-2 btn bg-red-gradient text-white px-3 px-md-4">
              Delete
            </button>
          </div>
          <div @click="cancelEdit()">
            <CButton class="me-2 btn-gradient-border">
              <span class="btn px-3 px-md-4"
                ><b class="text-graident-blue">Cancel</b></span
              >
            </CButton>
          </div>
        </div>
        <div
          v-else
          class="d-flex align-items-center justify-content-center"
          @click="submit(true)"
        >
          <CLoadingButton
            :spinner="isLoading"
            :disabled="isLoading"
            :loading="isLoading"
            class="me-2 btn bg-gradient-blue text-white"
          >
            Submit
          </CLoadingButton>
        </div>
      </CCol>
      <CCol col="12" md="7" xl="6">
        <div class="position-relative table-responsive mt-4 mt-md-0">
          <table v-if="periods" class="table bg-white">
            <thead>
              <tr>
                <th class="period-table-th position-relative pr-4" />
                <th class="period-table-th position-relative pr-4">Name</th>
                <th class="period-table-th position-relative pr-4">Status</th>
                <th class="period-table-th position-relative pr-4">Action</th>
              </tr>
            </thead>
            <draggable
              v-model="reOrderedPeriods"
              item-key="id"
              filter=".no-drag"
              tag="tbody"
              @end="saveOrder"
            >
              <template #item="{ element }">
                <tr class="w-100">
                  <td class="mw-auto">
                    <div class="fi flaticon-move" />
                  </td>
                  <td>{{ element.name }}</td>
                  <td class="no-drag">
                    <div
                      class="btn-status"
                      :class="{
                        active: element.status,
                        inactive: !element.status
                      }"
                    >
                      {{ element.status ? "Active" : "Inactive" }}
                    </div>
                  </td>
                  <td class="d-flex justify-content-center no-drag">
                    <button
                      class="px-2 px-md-3 py-1 rounded bg-light-blue text-white d-flex align-items-center border-0"
                      @click="editPeriod(element)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        class="bi bi-pencil"
                        viewBox="0 0 16 16"
                      >
                        <path
                          d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"
                        />
                      </svg>
                      <span class="ms-1 text-uppercase fw-bold">Edit</span>
                    </button>
                  </td>
                </tr>
              </template>
            </draggable>
          </table>
        </div>
      </CCol>
    </CRow>
  </CContainer>
</template>
<script>
import { mapGetters } from "vuex"
import draggable from "vuedraggable"
import useVuelidate from "@vuelidate/core"
import { required, maxLength } from "vuelidate/lib/validators"
import HelpCenterButton from "@/components/HelpCenterButton"

export default {
  name: "PeriodsDataTable",
  setup() {
    return { v$: useVuelidate() }
  },
  components: {
    draggable,
    HelpCenterButton
  },
  data() {
    return {
      isLoading: false,
      form: {
        name: null,
        status: null
      },
      periodStatuses: [
        {
          value: 1,
          label: "Active"
        },
        {
          value: 0,
          label: "InActive"
        }
      ],
      dataTableFields: [
        { key: "name", label: "Name" },
        { key: "status", label: "Status" },
        { key: "action", label: "Action" }
      ],
      reOrderedPeriods: [],
      pagination: {
        activePage: 1,
        total: 0,
        pages: 0,
        per_page: 5
      },
      reqResponse: {
        type: "",
        message: "",
        errors: {}
      }
    }
  },
  validations: {
    form: {
      name: {
        required,
        maxLength: maxLength(40)
      },
      status: {
        required
      }
    }
  },
  computed: {
    isValid() {
      return !this.v$.form.$invalid
    },
    ...mapGetters({
      periods: "periods/periods",
      editablePeriod: "periods/editablePeriod"
    })
  },
  watch: {
    editablePeriod: function (period) {
      this.setFormByPeriodRecord(period)
    },
    periods: function (data) {
      this.reOrderedPeriods = JSON.parse(JSON.stringify(data))
    },
    $route: function () {
      this.cancelEdit()
    }
  },
  mounted() {
    this.getPeriods()
    this.form.status = this.periodStatuses[0].value
  },
  methods: {
    getPeriods() {
      this.$store.dispatch("periods/getPeriods").then((res) => {
        this.reOrderedPeriods = JSON.parse(JSON.stringify(res))
      })
    },
    submit(isCreate) {
      if (!isCreate) {
        this.updatePeriods(this.editablePeriod.id)
      } else {
        this.createPeriod()
      }
    },
    createPeriod() {
      if (this.isValid) {
        this.resetResponseMessages()
        this.isLoading = true
        this.$store
          .dispatch("periods/createPeriod", this.form)
          .then(() => {
            this.reqResponse.userForm = {
              type: "success",
              message: "Successfully created."
            }
            this.isLoading = false
            this.resetForm()
            setTimeout(() => {
              this.$store.dispatch("periods/getPeriods", {
                clearCacheEntry: true
              })
            }, 800)
          })
          .catch((err) => {
            const response = err.response.data
            this.isLoading = false
            this.reqResponse = {
              type: "danger",
              message: response.message,
              errors: response.errors
            }
          })
      } else {
        this.v$.$touch()
      }
    },
    updatePeriods(periodId) {
      if (this.isValid) {
        this.resetResponseMessages()
        this.isLoading = true
        const data = { ...this.editablePeriod, ...this.form }
        this.$store
          .dispatch("periods/updatePeriod", { periodId, data })
          .then(() => {
            this.reqResponse.userForm = {
              type: "success",
              message: "Successfully updated!"
            }
            this.isLoading = false
            setTimeout(() => {
              this.$store.dispatch("periods/getPeriods", {
                clearCacheEntry: true
              })
              this.resetForm()
              this.$store.commit("periods/SET_EDITABLE_PERIOD", {})
              this.resetResponseMessages()
            }, 1000)
          })
          .catch((err) => {
            const response = err.response.data
            this.isLoading = false
            this.reqResponse = {
              type: "danger",
              message: response.message,
              errors: response.errors
            }
          })
      } else {
        this.v$.$touch()
      }
    },
    saveOrder() {
      const data = this.reOrderedPeriods.map((period, i) => {
        return {
          id: period.id,
          position: i + 1
        }
      })
      this.$store.dispatch("periods/updatePosition", data)
    },
    resetResponseMessages() {
      this.reqResponse = {
        type: "",
        message: "",
        errors: {}
      }
    },
    resetForm() {
      this.v$.$reset()
      this.form = {
        name: null,
        status: this.periodStatuses[0].value
      }
    },
    setFormByPeriodRecord(period) {
      if (period) {
        this.form.name = period.name
        this.form.status = period.status
      }
    },
    editPeriod(period) {
      this.$store.commit("periods/SET_EDITABLE_PERIOD", period)
      this.$helpers.scrollToTop()
    },
    deletePeriod() {
      this.$store
        .dispatch("periods/deletePeriod", { periodId: this.editablePeriod.id })
        .then(() => {
          setTimeout(() => {
            this.$store.dispatch("periods/getPeriods", {
              clearCacheEntry: true
            })
            this.resetForm()
            this.$store.commit("periods/SET_EDITABLE_PERIOD", null)
          }, 750)
        })
    },
    cancelEdit() {
      this.$store.commit("periods/SET_EDITABLE_PERIOD", null)
      this.resetForm()
      this.resetResponseMessages()
    }
  }
}
</script>
